import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import {
  VehicleMakeDropdownField,
  VehicleModelDropdownField,
  VehicleTypeDropdownField,
  YearDropdownField,
  LicensePlateField,
} from "@app.automotus.io/components/forms/VehicleInfoFields";
import { StateDropdownFormikField } from "./StateDropdownFormikField";
import { VehicleMake, VehicleModel } from "common/graphql/queries";

export interface AddVehicleFormContentProps {
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Whether the form is valid and can be submitted */
  isValid: boolean;
  /** Handler for form submission */
  onSubmit: () => void;
  /** Handler for cancel action */
  onCancel: () => void;
  /** Current make value (for cascading to model dropdown) */
  makeValue: VehicleMake | null;
  /** Current model value (for cascading to year dropdown) */
  modelValue: VehicleModel | null;
}

/**
 * Presentation component for the Add Vehicle form.
 * Displays all vehicle input fields and submit/cancel buttons.
 */
export const AddVehicleFormContent: React.FC<AddVehicleFormContentProps> = ({
  isSubmitting,
  isValid,
  onSubmit,
  makeValue,
  modelValue,
}) => {
  return (
    <Card variant="outlined" sx={{ p: 3, bgcolor: "grey.50" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Don&apos;t see your vehicle? Add it here and it will be saved to your account.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <VehicleMakeDropdownField name="make" label="Make" required disabled={isSubmitting} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VehicleModelDropdownField
            name="model"
            label="Model"
            makeId={makeValue?.makeId ?? null}
            required
            disabled={isSubmitting}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <YearDropdownField name="year" label="Year" modelId={modelValue?.modelId ?? null} disabled={isSubmitting} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VehicleTypeDropdownField name="vehicleType" label="Vehicle Type" required disabled={isSubmitting} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StateDropdownFormikField
            name="licensePlateState"
            label="License Plate State"
            placeholder="Select State"
            required
            disabled={isSubmitting}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LicensePlateField name="licensePlate" label="License Plate Number" required disabled={isSubmitting} />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting || !isValid}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{ flex: 1 }}
        >
          {isSubmitting ? "Adding..." : "Add Vehicle"}
        </Button>
      </Box>
    </Card>
  );
};

export default AddVehicleFormContent;
